import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../utils/api";

const airportMap = {
  heathrow: "HEATHROW",
  gatwick: "GATWICK",
  luton: "LUTON",
  stansted: "STANSTED",
  "east-midlands": "EMA",
  east_midlands: "EMA",
  airports_general: "HEATHROW",
  slough: "HEATHROW",
  windsor: "HEATHROW",
  maidenhead: "HEATHROW",
  london: "HEATHROW",
  "heathrow-london": "HEATHROW",
  "heathrow-city": "HEATHROW",
  legoland: "HEATHROW",
  thorpe: "HEATHROW",
};

const baseHeadlinesByVariant = {
  "heathrow-london": [
    {
      text: "🧳 Arriving at Heathrow? Check terminal, baggage and immigration guidance",
      to: "/arrival-assistant-visitor",
    },
    {
      text: "🚦 M4 and Central London traffic can change transfer times — view live journey checks",
      to: "/heathrow/status",
    },
    {
      text: "✈ Heathrow Terminal 2–5 group pickups — keep flight and meeting details ready",
      to: "/flight-checker",
    },
    {
      text: "🏨 Heading to a London hotel? Confirm the exact entrance and group loading point",
      to: "/heathrow-to-london",
    },
  ],
  "heathrow-city": [
    {
      text: "🧳 Arriving at Heathrow? Keep flight, terminal and group meeting details ready",
      to: "/arrival-assistant-visitor",
    },
    {
      text: "🚦 Long-distance group transfers are planned around live motorway conditions",
      to: "/heathrow/status",
    },
    {
      text: "✈ Heathrow Terminal 2–5 group pickups — allow time for baggage reclaim",
      to: "/flight-checker",
    },
  ],
  heathrow: [
    {
      text: "🚗 Heathrow drop-off charges increase to £7 from Jan 2026 — see free alternatives (tap to open)",
      to: "/heathrow-dropoff-free-2026",
    },
    {
      text: "🛂 Allow extra time at Terminal 5 during peak security hours — click for timing tips",
      to: "/how-early-to-arrive-heathrow",
    },
    {
      text: "🚦 M4 / M25 traffic can add 10–15 mins at rush hour — click to plan your leave time",
      to: "/leave-for-heathrow",
    },
    {
      text: "🚆 Taxi or train to Heathrow? Compare cost, time & luggage — click to compare",
      to: "/train-vs-taxi",
    },
    {
      text: "🚄 Heathrow Express vs Taxi — what’s actually quicker? Tap to see",
      to: "/heathrow-express-vs-taxi",
    },
    {
      text: "⏰ Not sure when to leave for Heathrow? Get an exact time — click to check",
      to: "/leave-for-heathrow",
    },
    {
      text: "🎢 Visiting Legoland Windsor? Fixed-price family transfers — tap to view fares",
      to: "/legoland-budget-planner",
    },
    {
      text: "🎢 Thorpe Park transfers — family and group fares from your postcode",
      to: "/thorpe-park-budget-planner",
    },
    {
      text: "💷 Looking for the cheapest way to Heathrow? See all options — click to compare",
      to: "/cheapest-way-to-heathrow",
    },
    {
      text: "🧳 Arriving at Heathrow? Step-by-step help from landing to home — tap to start",
      to: "/arrival-assistant-visitor",
    },
  ],
  slough: [
    {
      text: "🚗 Heathrow drop-off charges increase to £7 from Jan 2026 — see free alternatives (tap to open)",
      to: "/heathrow-dropoff-free-2026",
    },
    {
      text: "💷 Cheapest way from Slough to Heathrow? Compare all options — click to compare",
      to: "/cheapest-way-to-heathrow",
    },
    {
      text: "🚦 M4 / M25 traffic can add 10–15 mins at rush hour — plan ahead",
      to: "/leave-for-heathrow",
    },
    {
      text: "🚕 Slough ↔ Heathrow fixed fares — see routes",
      to: "/slough-to-heathrow",
    },
    {
      text: "⏰ Dropping at Heathrow? See exactly when to leave from Slough",
      to: "/leave-for-heathrow",
    },
    {
      text: "🧳 Flight & immigration checks before you go — view live status",
      to: "/flight-checker",
    },
    {
      text: "🚗 Eligible for free Heathrow drop-off? Check the guide",
      to: "/heathrow-dropoff-free-2026",
    },
    {
      text: "🎢 Legoland & Thorpe Park transfers — family pricing from Slough",
      to: "/legoland-budget-planner",
    },
  ],
  gatwick: [
    {
      text: "🚦 M25 / M23 traffic can add 10–20 mins — plan your Gatwick transfer",
      to: "/slough-to-gatwick",
    },
    {
      text: "💷 Compare Gatwick transfer options — taxi vs train vs coach",
      to: "/train-vs-taxi",
    },
    {
      text: "🎢 Legoland or Thorpe Park from Gatwick? See family fares",
      to: "/legoland-budget-planner",
    },
    {
      text: "ℹ️ Gatwick drop-off charge: £10 for 10 mins, then £1/min up to 20 mins — see details",
      to: "/travelnews",
    },
    {
      text: "📰 Gatwick travel & security updates — see the latest",
      to: "/travelnews",
    },
  ],
  luton: [
    {
      text: "🚦 M1 traffic can add 10–20 mins — plan your Luton transfer",
      to: "/slough-to-luton",
    },
    {
      text: "🧳 Flight & security checks — see live Heathrow-style tools",
      to: "/flight-checker",
    },
    {
      text: "ℹ️ Luton drop-off: up to 10 mins £7, then £1/min (max 30 mins) — see details",
      to: "/travelnews",
    },
    {
      text: "📰 Luton travel & terminal updates — see the latest",
      to: "/travelnews",
    },
  ],
  stansted: [
    {
      text: "🚦 M11 traffic can add 10–20 mins — plan your Stansted transfer",
      to: "/slough-to-stansted",
    },
    {
      text: "🧳 Flight & security checks — see live Heathrow-style tools",
      to: "/flight-checker",
    },
    {
      text: "ℹ️ Stansted drop-off: up to 15 mins £7, over 15 mins £25 — see details",
      to: "/travelnews",
    },
    {
      text: "📰 Stansted travel & security updates — see the latest",
      to: "/travelnews",
    },
  ],
  manchester: [
    {
      text: "✈ Manchester Airport group transfers — keep terminal, flight and luggage details ready",
      to: "/manchester-airport-minibus-hire",
    },
    {
      text: "🚦 M56 and M60 traffic can change airport transfer times — allow a sensible journey buffer",
      to: "/manchester-airport-minibus-hire",
    },
    {
      text: "🚐 8 or 16 seats? Include every passenger, suitcase and large item in your quote request",
      to: "/minibus-hire",
    },
    {
      text: "🚆 Compare rail and private transfer planning for Manchester Airport groups",
      to: "/train-vs-taxi-manchester-airport",
    },
  ],
  windsor: [
    {
      text: "🎡 Windsor & Legoland transfers — see family fares",
      to: "/legoland-budget-planner",
    },
    {
      text: "🚗 Windsor ↔ Heathrow drop-off tips — avoid the £7 charge",
      to: "/heathrow-dropoff-free-2026",
    },
    {
      text: "⏰ Leaving Windsor for Heathrow? Get an exact time",
      to: "/leave-for-heathrow",
    },
    {
      text: "🧳 Flight & immigration checks — view live status",
      to: "/flight-checker",
    },
    {
      text: "🎢 Thorpe Park transfers — group-friendly fares",
      to: "/thorpe-park-budget-planner",
    },
  ],
  maidenhead: [
    {
      text: "🌉 Maidenhead ↔ Heathrow fixed fares — view routes",
      to: "/heathrow-to-maidenhead",
    },
    {
      text: "💷 Cheapest way to Heathrow? Compare options",
      to: "/cheapest-way-to-heathrow",
    },
    {
      text: "⏰ Dropping at Heathrow? See exactly when to leave from Maidenhead",
      to: "/leave-for-heathrow",
    },
    {
      text: "🧳 Flight & immigration checks — view live status",
      to: "/flight-checker",
    },
    {
      text: "🎢 Legoland & Thorpe Park transfers — family fares",
      to: "/legoland-budget-planner",
    },
  ],
  london: [
    {
      text: "🚗 Heathrow drop-off charges increase to £7 from Jan 2026 — see free alternatives",
      to: "/heathrow-dropoff-free-2026",
    },
    {
      text: "⏰ Leaving London for Heathrow? Get an exact time",
      to: "/leave-for-heathrow",
    },
    {
      text: "🚆 Heathrow Express vs Taxi — what’s actually quicker?",
      to: "/heathrow-express-vs-taxi",
    },
    {
      text: "🧳 Flight & immigration checks before you go — view live status",
      to: "/flight-checker",
    },
    {
      text: "💷 Cheapest way to Heathrow? Compare all options",
      to: "/cheapest-way-to-heathrow",
    },
    {
      text: "🎢 Legoland & Thorpe Park transfers — family-friendly pricing",
      to: "/legoland-budget-planner",
    },
  ],
  legoland: [
    {
      text: "🎢 Legoland Windsor reopens 14 Feb — plan your day & tickets",
      to: "/legoland-budget-planner",
    },
    {
      text: "🌡️ Windsor (Legoland) live weather — check before you set off",
      to: "/legoland-budget-planner",
    },
    {
      text: "🚆 Train vs Taxi to Heathrow/Legoland — compare cost & time",
      to: "/train-vs-taxi",
    },
    {
      text: "🎢 Legoland vs Thorpe Park — which is cheaper today?",
      to: "/legoland-vs-thorpe-park",
    },
    {
      text: "🚖 Slough ↔ Legoland fixed fares — see routes",
      to: "/slough-taxi",
    },
    {
      text: "🚖 Windsor ↔ Legoland taxis — quick quote",
      to: "/windsor-taxi",
    },
  ],
  thorpe: [
    {
      text: "🎢 Thorpe Park reopens 27 Mar — get fares & timings",
      to: "/thorpe-park-budget-planner",
    },
    {
      text: "🌡️ Thorpe Park (Chertsey) live weather — check before you go",
      to: "/thorpe-park-budget-planner",
    },
    {
      text: "🚆 Train vs Taxi to Heathrow/Thorpe Park — compare fast",
      to: "/train-vs-taxi",
    },
    {
      text: "🎢 Legoland vs Thorpe Park — see family value picks",
      to: "/legoland-vs-thorpe-park",
    },
    {
      text: "🚖 Slough ↔ Thorpe Park taxis — fixed fares",
      to: "/slough-taxi",
    },
    {
      text: "🚖 Windsor ↔ Thorpe Park taxis — quick quote",
      to: "/windsor-taxi",
    },
  ],
  "east-midlands": [
    {
      text: "🧭 East Midlands live journey monitor — get exact leave time with disruption checks",
      to: "/east-midlands-airport/live-journey-planner",
    },
    {
      text: "🚗 EMA drop-off options: Rapid vs Short Stay vs Long Stay — compare charges now",
      to: "/east-midlands-airport/drop-off-calculator",
    },
    {
      text: "🚆 Train or taxi to EMA? Compare price, timing, and transfer friction",
      to: "/train-vs-taxi-east-midlands-airport",
    },
    {
      text: "📍 Going to East Midlands Airport? Use the full route guide and planner",
      to: "/how-to-get-to-east-midlands-airport",
    },
  ],
  airports_general: [
    {
      text: "🎓 Coming to the UK for studies? Use our arrival checklist",
      to: "/arrival-assistant-student",
    },
    {
      text: "🚆 Train vs Taxi — compare cost, time, and hassle for any UK route",
      to: "/train-vs-taxi",
    },
    {
      text: "🎢 Legoland budget planner — door-to-door costs with luggage",
      to: "/legoland-budget-planner",
    },
    {
      text: "🎢 Thorpe Park budget planner — fares for families and groups",
      to: "/thorpe-park-budget-planner",
    },
    {
      text: "🚌 Minibus hire — fixed prices for 8–16 seaters",
      to: "/minibus-hire",
    },
    {
      text: "🚌 Coach hire — quotes for larger groups and events",
      to: "/coach-hire",
    },
  ],
};

const LiveStrip = ({ variant = "heathrow", desktopDelayMs = 4000, mobileDelayMs = 6000 }) => {
  const baseHeadlines = baseHeadlinesByVariant[variant] || baseHeadlinesByVariant.heathrow;
  const [flightHeadlines, setFlightHeadlines] = useState([]);
  const [weatherHeadlines, setWeatherHeadlines] = useState([]);
  const combinedHeadlines = useMemo(
    () => [...flightHeadlines, ...weatherHeadlines, ...baseHeadlines],
    [flightHeadlines, weatherHeadlines, baseHeadlines]
  );

  const [liveIndex, setLiveIndex] = useState(() => (combinedHeadlines.length ? Date.now() % combinedHeadlines.length : 0));
  const [isHovered, setIsHovered] = useState(false);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth <= 768 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (combinedHeadlines.length === 0) return;
    setLiveIndex((prev) => prev % combinedHeadlines.length);
  }, [combinedHeadlines.length]);

  const formatFlightHeadline = (flight, type = "arrival") => {
    if (!flight) return null;
    const code = flight.iata_flight_identifier || flight.flight_number || flight.id || "Flight";
    const city =
      type === "arrival"
        ? flight.origin_city || flight.origin_airport_name || "Origin"
        : flight.destination_city || flight.destination_airport_name || "Destination";
    const time = flight.scheduled_local
      ? new Date(flight.scheduled_local).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : "Time TBC";
    const terminal = flight.terminal || "TBC";
    const status = flight.status_message || flight.status || "Scheduled";

    if (type === "arrival") {
      return {
        text: `🛬 Arrivals: ${code} from ${city} · ${time} · T${terminal} · ${status} — See immigration & pickup →`,
        to: "/flight-checker",
      };
    }
    return {
      text: `🛫 Departures: ${code} to ${city} · ${time} · T${terminal} · ${status} — See security & timing →`,
      to: "/flight-checker",
    };
  };

  useEffect(() => {
    if (variant !== "heathrow") return;
    let cancelled = false;
    const fetchFlights = async () => {
      try {
        const [arrRes, depRes] = await Promise.all([
          api.get("/api/flights/next-arrivals"),
          api.get("/api/flights/next-departures"),
        ]);

        const pickNext = (list = []) => {
          if (!Array.isArray(list) || list.length === 0) return null;
          const sorted = [...list].sort((a, b) => {
            const ta = a.scheduled_local ? new Date(a.scheduled_local).getTime() : Infinity;
            const tb = b.scheduled_local ? new Date(b.scheduled_local).getTime() : Infinity;
            return ta - tb;
          });
          return sorted[0] || null;
        };

        const nextArrival = pickNext(arrRes.data?.flights || []);
        const nextDeparture = pickNext(depRes.data?.flights || []);

        if (!cancelled) {
          const newHeadlines = [formatFlightHeadline(nextArrival, "arrival"), formatFlightHeadline(nextDeparture, "departure")].filter(
            Boolean
          );
          setFlightHeadlines(newHeadlines);
        }
      } catch (err) {
        if (!cancelled) {
          setFlightHeadlines([]);
        }
      }
    };

    fetchFlights();
    const interval = setInterval(fetchFlights, 10 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [variant]);

  useEffect(() => {
    if (variant === "manchester") {
      setWeatherHeadlines([]);
      return undefined;
    }
    let cancelled = false;
    const airport = airportMap[variant] || "HEATHROW";
    const weatherRouteByVariant = {
      "east-midlands": "/east-midlands-airport/live-journey-planner",
      east_midlands: "/east-midlands-airport/live-journey-planner",
    };
    const weatherRoute = weatherRouteByVariant[variant] || "/arrival-assistant-visitor";
    const fetchWeather = async () => {
      try {
        const res = await api.get(`/api/open/weather/brief?airport=${airport}`);
        if (cancelled) return;
        const w = res.data;
        if (w?.headline) {
          setWeatherHeadlines([
            {
              text: `🌦️ Weather — ${w.headline} · Tap for more`,
              to: weatherRoute,
            },
          ]);
        }
      } catch (err) {
        if (!cancelled) setWeatherHeadlines([]);
      }
    };

    fetchWeather();
    const interval = setInterval(fetchWeather, 3 * 60 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [variant]);

  useEffect(() => {
    if (isHovered) return;
    if (combinedHeadlines.length === 0) return;
    const delay = isMobile ? mobileDelayMs : desktopDelayMs;
    const interval = setInterval(() => {
      setLiveIndex((prev) => (prev + 1) % combinedHeadlines.length);
    }, delay);
    return () => clearInterval(interval);
  }, [isHovered, isMobile, combinedHeadlines.length, desktopDelayMs, mobileDelayMs]);

  if (combinedHeadlines.length === 0) return null;
  const safeIndex = combinedHeadlines.length ? liveIndex % combinedHeadlines.length : 0;
  const currentHeadline = combinedHeadlines[safeIndex];
  if (!currentHeadline) return null;

  const labelMap = {
    heathrow: "Heathrow Live",
    "east-midlands": "East Midlands Live",
    gatwick: "Gatwick Live",
    manchester: "Manchester Airport",
    slough: "Slough Live",
    legoland: "Legoland Live",
    thorpe: "Thorpe Park Live",
    airports_general: "Travel tools",
  };
  const label = labelMap[variant] || "Live";

  return (
    <div
      className="home-live-strip"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={isMobile ? { marginBottom: 10 } : undefined}
    >
      <div
        className="home-live-strip-inner"
        style={
          isMobile
            ? {
                padding: "10px 14px",
                gap: 12,
                minHeight: "auto",
              }
            : undefined
        }
      >
        <span
          className="home-live-label"
          style={
            isMobile
              ? {
                  padding: "8px 12px",
                  fontSize: 13,
                  lineHeight: 1.1,
                }
              : undefined
          }
        >
          {label}
        </span>
        <Link
          className="home-live-headline"
          to={currentHeadline.to}
          style={
            isMobile
              ? {
                  fontSize: 13,
                  lineHeight: 1.3,
                }
              : undefined
          }
        >
          {currentHeadline.text}
        </Link>
      </div>
    </div>
  );
};

export default LiveStrip;
